import { useState, useEffect } from 'react';
import api from '../services/api';

interface ExpenseCategory {
  id: string;
  code: string;
  category_name: string;
  category_type: string;
  single_claim_limit: number;
  is_active: boolean;
}

export default function ExpenseCategories() {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    categoryName: '',
    categoryType: 'Other',
    singleClaimLimit: 10000,
    isTaxable: false
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ecs/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch expense categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/ecs/categories', formData);
      setFormData({
        code: '',
        categoryName: '',
        categoryType: 'Other',
        singleClaimLimit: 10000,
        isTaxable: false
      });
      setShowForm(false);
      fetchCategories();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create expense category');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Expense Categories</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : 'Create Category'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Category Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="px-4 py-2 border rounded-lg"
                required
              />
              <input
                type="text"
                placeholder="Category Name"
                value={formData.categoryName}
                onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
                className="px-4 py-2 border rounded-lg"
                required
              />
              <select
                value={formData.categoryType}
                onChange={(e) => setFormData({ ...formData, categoryType: e.target.value })}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="Travel">Travel</option>
                <option value="Meals">Meals</option>
                <option value="Accommodation">Accommodation</option>
                <option value="Other">Other</option>
              </select>
              <input
                type="number"
                placeholder="Single Claim Limit"
                value={formData.singleClaimLimit}
                onChange={(e) => setFormData({ ...formData, singleClaimLimit: parseFloat(e.target.value) })}
                className="px-4 py-2 border rounded-lg"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isTaxable}
                  onChange={(e) => setFormData({ ...formData, isTaxable: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Is Taxable</span>
              </label>
            </div>
            <button
              type="submit"
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Create Category
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-center text-gray-600">Loading expense categories...</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">Code</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Category Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Type</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Claim Limit</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat.id} className="border-b hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-semibold">{cat.code}</td>
                  <td className="px-6 py-3 text-sm">{cat.category_name}</td>
                  <td className="px-6 py-3 text-sm">{cat.category_type}</td>
                  <td className="px-6 py-3 text-sm">${cat.single_claim_limit.toLocaleString()}</td>
                  <td className="px-6 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${cat.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {cat.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
